import React, { useEffect, useState, useRef } from 'react';
import Layout from '../../components/layout/Layout';
import { getData, deleteData, getPresignedUrl, uploadToS3, postData, putData } from '../../services/apiService';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Modal, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography,
    IconButton, Button, TextField, Select, MenuItem, Pagination, InputAdornment,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Progress from '../../hooks/useProgressBar';
import useDebounce from '../../hooks/useDebounce';

const AddCourse = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [courseDetails, setCourseDetails] = useState([]);
    const [allUserData, setAllUserData] = useState([]);
    const [allInstructorData, setAllInstructorData] = useState([]);
    const isFetched = useRef(false);
    const [showCourseModal, addCourseShowModal] = useState(false);
    const [updateCourseModalShow, updateCourseModal] = useState(false);
    const [courseDetailsModalShow, courseDetailsModal] = useState(false);
    const [formData, setFormData] = useState({
        courseName: "", title: "", code: "", description: "", category: "",
        thumbnail: "", status: "", approvedBy: "", startDate: "", endDate: "", amount: "", tax: '',
    });
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [departmentData, setDepartmentData] = useState([]);
    const [errors, setErrors] = useState({});
    const ErrorMessage = ({ error }) => (
        error ? <div className="text-danger small mt-1">{error}</div> : null
    );

    // Pagination and search states
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearchQuery = useDebounce(searchQuery, 500);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        if (!isFetched.current) {
            fetchData();
            getDepartment();
            getAllUserAndInstructorData();
        }
    }, [page, rowsPerPage, debouncedSearchQuery]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await getData(`/get-all-courses?page=${page}&limit=${rowsPerPage}&search=${debouncedSearchQuery}`);
            if (response) {
                setData(response.data);
                setTotalPages(response.totalPages);
            }
            setLoading(false);
        } catch (error) {
            console.error('Failed to fetch data', error);
            setLoading(false);
        }
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setPage(1);
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(event.target.value);
        setPage(1);
    };

    const getAllUserAndInstructorData = async () => {
        try {
            const response = await getData('/get-all-user-and-instructorData');
            if (response) {
                setAllUserData(response.allUser);
                setAllInstructorData(response.instructors);
            }
        } catch (error) {
            console.error('Failed to fetch data', error);
        }
    };

    const getDepartment = async () => {
        try {
            const response = await getData("/get-departments-for-dropdown");
            if (Array.isArray(response)) {
                setDepartmentData(response);
            }
        } catch (error) {
            console.error("Failed to fetch users", error);
        }
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const { uploadUrl, fileKey, publicUrl } = await getPresignedUrl(file.type);

            await uploadToS3(uploadUrl, file);

            setFormData(prev => ({
                ...prev,
                thumbnail: publicUrl,
                fileKey
            }));
        } catch (error) {
            console.error('Error uploading file:', error);
        }
    };

    const addCourse = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await postData('/create-course', formData);
            setFormData({
                courseName: "", title: "", code: "", description: "", category: "",
                thumbnail: "", status: "", approvedBy: "", startDate: "",
                endDate: "", amount: "", tax: '', fileKey: ""
            });
            setErrors([]);
            addCourseShowModal(false);
            fetchData();
            setLoading(false);
        } catch (error) {
            if (error.response?.data?.errors) {
                const errorsObj = {};
                error.response.data.errors.forEach(err => {
                    errorsObj[err.path] = err.msg;
                });
                setErrors(errorsObj);
            }
            setTimeout(() => {
                setErrors('');
            }, 3000)
            setLoading(false);
        }
    };

    const editCourse = (id) => {
        const courseData = data.find(course => course._id === id);
        setSelectedCourse({
            ...courseData,
            thumbnailPreview: courseData.imageUrl ? courseData.imageUrl : null
        });
        updateCourseModal(true);
    };

    const handleUpdateFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        try {
            const { uploadUrl, fileKey, publicUrl } = await getPresignedUrl(file.type);

            await uploadToS3(uploadUrl, file);

            setSelectedCourse(prev => ({
                ...prev,
                thumbnail: publicUrl,
                fileKey,
                thumbnailPreview: URL.createObjectURL(file)
            }));
        } catch (error) {
            console.error('Error uploading file:', error);
        }
    };

    const updateCourse = async () => {
        if (!selectedCourse) return;
        setLoading(true);

        try {
            const updateData = {
                courseName: selectedCourse.courseName,
                code: selectedCourse.code,
                title: selectedCourse.title,
                description: selectedCourse.description,
                amount: selectedCourse.amount,
                tax: selectedCourse.tax,
                category: selectedCourse.category,
                status: selectedCourse.status,
                approvedBy: selectedCourse.approvedBy?._id || selectedCourse.approvedBy,
                startDate: selectedCourse.startDate,
                endDate: selectedCourse.endDate,
                thumbnail: selectedCourse.thumbnail,
                fileKey: selectedCourse.fileKey
            };

            await putData(`/update-course/${selectedCourse._id}`, updateData);

            fetchData();
            updateCourseModal(false);
            setLoading(false);
        } catch (error) {
            console.error('Failed to update course', error);
            setLoading(false);
        }
    };

    const deleteCourse = async (id) => {
        try {
            await deleteData(`/delete-course/${id}`);
            await fetchData();
        } catch (error) {
            console.error("Error:", error);
        }
    };

    const viewCourseDetails = (id) => {
        setCourseDetails(data.find(course => course._id === id));
        courseDetailsModal(true);
    };

    return (
        <Layout>
            {loading ? <Progress /> :
                <Box className="p-6 bg-gray-100 min-h-screen">
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mb: 3 }}>Add Course</Typography>
                    {/* Header: Add Button (left) and Search (right) */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2, }} >
                        <Button variant="contained" sx={{
                            borderRadius: 2, fontWeight: 600, boxShadow: 2, px: 3, py: 1, textTransform: 'none', backgroundColor: '#4B49AC',
                            '&:hover': { backgroundColor: '#3A3897', },
                        }} onClick={() => addCourseShowModal(true)} > + Add Course </Button>
                        <TextField placeholder="Search Departments" value={searchQuery} onChange={handleSearchChange} variant="outlined" size="medium"
                            sx={{ width: 350, bgcolor: '#e9ecef', borderRadius: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, }, }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#757575' }} />
                                    </InputAdornment>
                                ),
                                sx: { bgcolor: '#e9ecef', borderRadius: 2 },
                            }}
                        />
                    </Box>
                    {/* <TableContainer component={Paper} elevation={0} sx={{ mb: 2, borderRadius: 2, boxShadow: 2, overflow: 'hidden', }} > */}

                    <TableContainer component={Paper}
                        sx={{
                            width: "100%",
                            maxWidth: "100%",
                            overflowX: "auto",
                            overflowY: "hidden",
                            "&::-webkit-scrollbar": {
                                height: "8px"
                            },
                            "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "#888",
                                borderRadius: "4px"
                            }
                        }}
                    >
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#4B49AC' }}>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600, borderTopLeftRadius: 8 }}> ID </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Course Name </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Code </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Tille </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Amount </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Tax </TableCell>
                                    {/* <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Course Type </TableCell> */}
                                    {/* <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Department Name </TableCell> */}
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Thumbnail </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600, borderTopRightRadius: 8 }}> Action </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.map((item, index) => (
                                    <TableRow key={item._id}>
                                        <TableCell align="center">
                                            <Typography variant="body1">{(page - 1) * rowsPerPage + index + 1}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.courseName}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.code}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.title}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.amount}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.tax}%</Typography>
                                        </TableCell>
                                        {/* <TableCell align="center">
                                            <Typography variant="body1">{item.courseType}</Typography>
                                        </TableCell> */}
                                        {/* <TableCell align="center">
                                            <Typography variant="body1">{item.department_id.name}</Typography>
                                        </TableCell> */}
                                        <TableCell align="center">
                                            <Typography variant="body1"><img src={item.imageUrl} alt="" className="img-fluid" style={{ width: "60px", height: "70px" }} /></Typography>
                                            {/* <Typography variant="body1"><img src={`${imageUrl}${item.thumbnail}`} alt="" className="img-fluid" style={{ width: "60px", height: "70px" }} /></Typography> */}
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton color="primary" onClick={() => editCourse(item._id)}>
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton color="error" onClick={() => deleteCourse(item._id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                            <IconButton color="success" onClick={() => viewCourseDetails(item._id)}>
                                                <VisibilityIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {/* Pagination */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <Pagination count={totalPages} page={page} onChange={handlePageChange} shape="rounded"
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    '&.Mui-selected': {
                                        bgcolor: '#4B49AC',
                                        color: 'white',
                                    },
                                },
                            }}
                        />
                        <Select value={rowsPerPage} onChange={handleRowsPerPageChange} size="small"
                            sx={{ ml: 2, height: 35, bgcolor: '#f7f7f7', border: '1px solid #e2e2e2', '& .MuiSelect-select': { py: 0.75 }, }}
                            IconComponent={KeyboardArrowDownIcon} >
                            <MenuItem value={10}>10 / page</MenuItem>
                            <MenuItem value={20}>20 / page</MenuItem>
                            <MenuItem value={50}>50 / page</MenuItem>
                        </Select>
                    </Box>

                    {/* Course Add model */}
                    <Modal show={showCourseModal} onHide={() => addCourseShowModal(false)} size="lg" style={{ zIndex: 9999 }} backdrop="static" keyboard={false} >
                        <Modal.Header closeButton>
                            <Modal.Title>Add Course</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <form onSubmit={addCourse}>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="courseName" className="col-form-label">Course Name: <span style={{ color: 'red' }}>*</span></label>
                                        <input type="text" className={`form-control ${errors.courseName ? 'is-invalid' : ''}`} placeholder="Course Name" id="courseName" name="courseName" value={formData.courseName} onChange={handleInputChange} />
                                        <ErrorMessage error={errors.courseName} />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="code" className="col-form-label">Course Code: <span style={{ color: 'red' }}>*</span></label>
                                        <input type="text" className={`form-control ${errors.code ? 'is-invalid' : ''}`} id="code" placeholder="code" name='code' value={formData.code} onChange={handleInputChange} />
                                        <ErrorMessage error={errors.code} />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="title" className="col-form-label">Title: <span style={{ color: 'red' }}>*</span></label>
                                        <textarea type="text" className={`form-control ${errors.title ? 'is-invalid' : ''}`} id="title" placeholder="Title" name='title' value={formData.title} onChange={handleInputChange} />
                                        <ErrorMessage error={errors.title} />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="description" className="col-form-label">Description: <span style={{ color: 'red' }}>*</span></label>
                                        <textarea type="text" className={`form-control ${errors.description ? 'is-invalid' : ''}`} id="description" placeholder="Description" name='description' value={formData.description} onChange={handleInputChange} />
                                        <ErrorMessage error={errors.description} />
                                    </div>
                                </div>

                                {/* <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="col-form-label">Department: <span style={{ color: 'red' }}>*</span></label>
                                        <select className={`form-select ${errors.department_id ? 'is-invalid' : ''}`} name="department_id" value={formData.department_id} onChange={handleInputChange}>
                                            <option value="">Select Department</option>
                                            {departmentData.map((department) => (
                                                <option key={department._id} value={department._id}>{department.name}</option>
                                            ))}
                                        </select>
                                        <ErrorMessage error={errors.department_id} />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="col-form-label">Instructor: <span style={{ color: 'red' }}>*</span></label>
                                        <select className={`form-select ${errors.instructor ? 'is-invalid' : ''}`} name="instructor" value={formData.instructor} onChange={handleInputChange}>
                                            <option value="">Select Instructor</option>
                                            {allInstructorData.map((instructorData) => (
                                                <option key={instructorData._id} value={instructorData._id}>{instructorData.firstName}</option>
                                            ))}
                                        </select>
                                        <ErrorMessage error={errors.instructor} />
                                    </div>
                                </div> */}

                                <div className="row">
                                    {/* <div className="col-md-6 mb-3">
                                        <label className="col-form-label">Approved By:</label>
                                        <select className="form-select" name="approvedBy" value={formData.approvedBy} onChange={handleInputChange}>
                                            <option value="">Select Approved By</option>
                                            {allUserData.map((userData) => (
                                                <option key={userData._id} value={userData._id}>{userData.firstName}</option>
                                            ))}
                                        </select>
                                    </div> */}
                                    {/* <div className="col-md-6 mb-3">
                                        <label className="col-form-label">Course Type:</label>
                                        <select className="form-select" name="courseType" value={formData.courseType} onChange={handleInputChange}>
                                            <option value="">Select Course Type</option>
                                            <option value="paid">Paid</option>
                                            <option value="free">Free</option>
                                        </select>
                                    </div> */}
                                    <div className="col-md-6 mb-3">
                                        <label className="col-form-label">Status:</label>
                                        <select className="form-select" name="status" value={formData.status} onChange={handleInputChange}>
                                            <option value="">Select Status</option>
                                            <option value="draft">Draft</option>
                                            <option value="pending_approval">Pending Approval</option>
                                            <option value="published">Published</option>
                                            <option value="rejected">Rejected</option>
                                        </select>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="category" className="col-form-label">Category: <span style={{ color: 'red' }}>*</span></label>
                                        <input type="text" className={`form-control ${errors.category ? 'is-invalid' : ''}`} id="category" placeholder="Category" name='category' value={formData.category} onChange={handleInputChange} />
                                        <ErrorMessage error={errors.category} />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="amount" className="col-form-label">Amount: <span style={{ color: 'red' }}>*</span></label>
                                        <input type="number" className={`form-control ${errors.amount ? 'is-invalid' : ''}`} id="amount" placeholder="Amount" name='amount' value={formData.amount} onChange={handleInputChange} />
                                        <ErrorMessage error={errors.amount} />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="tax" className="col-form-label">Tax (%):</label>
                                        <input type="number" className="form-control" id="tax" placeholder="Tax" name='tax' value={formData.tax} onChange={handleInputChange} />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="startDate" className="col-form-label">Start Date:</label>
                                        <input type="date" className="form-control" id="startDate" placeholder="Start Date" name='startDate' value={formData.startDate} onChange={handleInputChange} />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="endDate" className="col-form-label">End Date:</label>
                                        <input type="date" className="form-control" id="endDate" placeholder="endDate" name='endDate' value={formData.endDate} onChange={handleInputChange} />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="thumbnail" className="col-form-label">Thumbnail:</label>
                                        <input type="file" className="form-control" id="thumbnail" name='thumbnail' onChange={handleFileChange} />
                                    </div>
                                </div>
                                {/* <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="col-form-label">Course Type:</label>
                                        <select className="form-select" name="courseType" value={formData.courseType} onChange={handleInputChange}>
                                            <option value="">Select Course Type</option>
                                            <option value="paid">Paid</option>
                                            <option value="free">Free</option>
                                        </select>
                                    </div>
                                </div> */}

                                <Modal.Footer>
                                    <Button variant="contained" type="submit" color="primary" sx={{ bgcolor: '#3A3897', '&:hover': { bgcolor: '#2A2878', } }}>Save</Button>
                                </Modal.Footer>
                            </form>
                        </Modal.Body>
                    </Modal>

                    {/* Course update model */}
                    <Modal show={updateCourseModalShow} onHide={() => updateCourseModal(false)} size="lg" style={{ zIndex: 9999 }} backdrop="static" keyboard={false} >
                        <Modal.Header closeButton>
                            <Modal.Title>Update Course</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {/* thumbnail: "", */}
                            <form>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="college-name" className="col-form-label">Course Name:</label>
                                        <input type="text" className="form-control" value={selectedCourse?.courseName || ""} onChange={(e) => setSelectedCourse({ ...selectedCourse, courseName: e.target.value })} required />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="address" className="col-form-label">Course Code:</label>
                                        <input type="text" className="form-control" value={selectedCourse?.code || ""} onChange={(e) => setSelectedCourse({ ...selectedCourse, code: e.target.value })} required />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="address" className="col-form-label">Title:</label>
                                        <textarea type="text" className="form-control" value={selectedCourse?.title || ""} onChange={(e) => setSelectedCourse({ ...selectedCourse, title: e.target.value })} required />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="description" className="col-form-label">Description:</label>
                                        <textarea type="text" className="form-control" value={selectedCourse?.description || ""} onChange={(e) => setSelectedCourse({ ...selectedCourse, description: e.target.value })} required />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="col-form-label">Status:</label>
                                        <select className="form-select" name="status" value={selectedCourse?.status} onChange={(e) => setSelectedCourse({ ...selectedCourse, status: e.target.value })} >
                                            <option value="">Select Status</option>
                                            <option value="draft">Draft</option>
                                            <option value="pending_approval">Pending Approval</option>
                                            <option value="published">Published</option>
                                            <option value="rejected">Rejected</option>
                                        </select>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="category" className="col-form-label">Category:</label>
                                        <input type="text" className="form-control" value={selectedCourse?.category || ""} onChange={(e) => setSelectedCourse({ ...selectedCourse, category: e.target.value })} required />
                                    </div>
                                    {/* <div className="col-md-6 mb-3">
                                        <label className="col-form-label">Course Type:</label>
                                        <select className="form-select" name="courseType" value={selectedCourse?.courseType || ""} onChange={(e) => setSelectedCourse({ ...selectedCourse, courseType: e.target.value })}>
                                            <option value="">Select Course Type</option>
                                            <option value="paid">Paid</option>
                                            <option value="free">Free</option>
                                        </select>
                                    </div> */}
                                    {/* <div className="col-md-6 mb-3">
                                        <label className="col-form-label">Department:</label>
                                        <select className="form-select" name="department_id" value={selectedCourse?.department_id?._id} onChange={(e) => setSelectedCourse({ ...selectedCourse, department_id: e.target.value })} >
                                            <option value="">Select Department</option>
                                            {departmentData.map((department) => (
                                                <option key={department._id} value={department._id}>{department.name}</option>
                                            ))}
                                        </select>
                                    </div> */}
                                </div>
                                <div className="row">
                                    {/* <div className="col-md-6 mb-3">
                                        <label className="col-form-label">Instructor:</label>
                                        <select className="form-select" name="instructor" value={selectedCourse?.instructor?._id} onChange={(e) => setSelectedCourse({ ...selectedCourse, instructor: e.target.value })} >
                                            <option value="">Select Department</option>
                                            {allInstructorData.map((instructorData) => (
                                                <option key={instructorData._id} value={instructorData._id}>{instructorData.firstName}</option>
                                            ))}
                                        </select>
                                    </div> */}
                                    {/* <div className="col-md-6 mb-3">
                                        <label className="col-form-label">Approved By:</label>
                                        <select className="form-select" name="approvedBy" value={selectedCourse?.approvedBy?._id} onChange={(e) => setSelectedCourse({ ...selectedCourse, approvedBy: e.target.value })} >
                                            <option value="">Select Department</option>
                                            {allUserData.map((userData) => (
                                                <option key={userData._id} value={userData._id}>{userData.firstName}</option>
                                            ))}
                                        </select>
                                    </div> */}
                                </div>

                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="amount" className="col-form-label">Amount:</label>
                                        <input type="number" className="form-control" value={selectedCourse?.amount || ""} onChange={(e) => setSelectedCourse({ ...selectedCourse, amount: e.target.value })} required />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="tax" className="col-form-label">Tax:</label>
                                        <input type="number" className="form-control" value={selectedCourse?.tax || ""} onChange={(e) => setSelectedCourse({ ...selectedCourse, tax: e.target.value })} required />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="college-name" className="col-form-label">Start Date:</label>
                                        <input type="date" className="form-control" value={selectedCourse?.startDate ? new Date(selectedCourse.startDate).toISOString().split("T")[0] : ""} onChange={(e) => setSelectedCourse({ ...selectedCourse, startDate: e.target.value })} required />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="college-name" className="col-form-label">End Date:</label>
                                        <input type="date" className="form-control" value={selectedCourse?.endDate ? new Date(selectedCourse.endDate).toISOString().split("T")[0] : ""} onChange={(e) => setSelectedCourse({ ...selectedCourse, endDate: e.target.value })} required />
                                    </div>
                                </div>



                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="thumbnail" className="col-form-label">Thumbnail:</label>
                                        <input
                                            type="file"
                                            className="form-control"
                                            id="thumbnail"
                                            name="thumbnail"
                                            accept="image/*"
                                            onChange={handleUpdateFileChange}
                                        />
                                        {selectedCourse?.thumbnailPreview && (
                                            <div className="mt-2">
                                                <img
                                                    src={selectedCourse.thumbnailPreview}
                                                    alt="Thumbnail preview"
                                                    style={{ width: '100px', height: 'auto' }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="col-form-label">Course Type:</label>
                                        <select className="form-select" name="courseType" value={selectedCourse?.courseType || ""} onChange={(e) => setSelectedCourse({ ...selectedCourse, courseType: e.target.value })}>
                                            <option value="">Select Course Type</option>
                                            <option value="paid">Paid</option>
                                            <option value="free">Free</option>
                                        </select>
                                    </div>
                                </div> */}
                                <Modal.Footer>
                                    <Button variant="contained" onClick={updateCourse} color="primary" sx={{ bgcolor: '#3A3897', '&:hover': { bgcolor: '#2A2878', } }}>Save changes</Button>
                                </Modal.Footer>
                            </form>
                        </Modal.Body>
                    </Modal>


                    <Modal show={courseDetailsModalShow} onHide={() => courseDetailsModal(false)} size="lg" style={{ zIndex: 9999 }} backdrop="static" keyboard={false} >
                        <Modal.Header closeButton>
                            <Modal.Title>Course Details</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div className="container">
                                <Row className="mb-2">
                                    <Col sm={4} className="fw-bold">Course Name:</Col>
                                    <Col sm={8}>{courseDetails?.courseName}</Col>
                                </Row>
                                <Row className="mb-2">
                                    <Col sm={4} className="fw-bold">Code:</Col>
                                    <Col sm={8}>{courseDetails?.code}</Col>
                                </Row>
                                <Row className="mb-2">
                                    <Col sm={4} className="fw-bold">Amount:</Col>
                                    <Col sm={8}>{courseDetails?.amount}</Col>
                                </Row>
                                {/* <Row className="mb-2">
                                    <Col sm={4} className="fw-bold">Course Type:</Col>
                                    <Col sm={8}>{courseDetails?.courseType}</Col>
                                </Row> */}
                                {/* <Row className="mb-2">
                                    <Col sm={4} className="fw-bold">Instructor:</Col>
                                    <Col sm={8}>{courseDetails?.instructor?.firstName} {courseDetails?.instructor?.lastName}</Col>
                                </Row> */}
                                <Row className="mb-2">
                                    <Col sm={4} className="fw-bold">Approved By:</Col>
                                    <Col sm={8}>{courseDetails?.approvedBy?.firstName} {courseDetails?.approvedBy?.lastName}</Col>
                                </Row>
                                <Row className="mb-2">
                                    <Col sm={4} className="fw-bold">Start Date:</Col>
                                    <Col sm={8}>{new Date(courseDetails?.startDate).toLocaleDateString()}</Col>
                                </Row>
                                <Row className="mb-2">
                                    <Col sm={4} className="fw-bold">End Date:</Col>
                                    <Col sm={8}>{new Date(courseDetails?.endDate).toLocaleDateString()}</Col>
                                </Row>
                                <Row className="mb-2">
                                    <Col sm={4} className="fw-bold">Description:</Col>
                                    <Col sm={8}>{courseDetails?.description} </Col>
                                </Row>
                            </div>
                        </Modal.Body>
                    </Modal>
                </Box>
            }
        </Layout>
    );
};

export default AddCourse;